<template>
  <AppLayout :title="trans('Kontakt & Hilfe')">
    <div class="min-h-screen bg-gray-50 py-12">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Header Section -->
        <div class="text-center mb-12">
          <h1 class="text-3xl font-bold text-gray-900 mb-4">{{ $t('Kontakt & Hilfe') }}</h1>
          <p class="text-lg text-gray-600">{{ $t('Haben Sie Fragen? Wir sind für Sie da!') }}</p>
        </div>

        <!-- Flash Messages -->
        <div v-if="$page.props.flash.message" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <div class="bg-green-50 border border-green-200 rounded-lg p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm font-medium text-green-800">
                  {{ $page.props.flash.message.text }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div v-if="$page.props.flash.error" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <div class="bg-red-50 border border-red-200 rounded-lg p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm font-medium text-red-800">
                  {{ $page.props.flash.error.text }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <!-- Contact Form Section -->
          <div class="bg-white rounded-xl shadow-lg p-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-6">{{ $t('Nachricht senden') }}</h2>
            
            <!-- Customer Information Display -->
            <div class="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ $t('Ihre Kontaktdaten') }}</h3>
              <div class="space-y-2 text-sm">
                <div><strong>{{ $t('Kunde') }}:</strong> {{ customerName }} ({{ customerNumber }})</div>
                <div><strong>{{ $t('Name') }}:</strong> {{ userName }}</div>
                <div><strong>{{ $t('E-Mail') }}:</strong> {{ userEmail }}</div>
                <div v-if="orderNumber"><strong>{{ $t('Bestellnummer') }}:</strong> {{ orderNumber }}</div>
              </div>
            </div>

            <!-- Contact Form -->
            <form 
              action="https://sonnenglas.sirportly.com/remote_form/1591" 
              method="POST"
              @submit="handleSubmit"
              class="space-y-6"
            >
              <!-- Anti-spam hidden field -->
              <input type="text" name="checkf" value="" style="display:none" />
              
              <!-- Contact Type (hidden, set to b2b for partner portal) -->
              <input type="hidden" name="ticket[custom][contact_type]" value="b2b" />

              <!-- Hidden Fields (pre-filled user and customer data) -->
              <input type="hidden" name="ticket[name]" v-model="form.name" />
              <input type="hidden" name="ticket[email]" v-model="form.email" />
              <input type="hidden" name="ticket[custom][customer_name]" v-model="form.customerName" />
              <input type="hidden" name="ticket[custom][customer_number]" v-model="form.customerNumber" />

              <!-- Order Number Field (optional) -->
              <div>
                <label for="form1591-custom-order_number" class="block text-sm font-medium text-gray-700 mb-2">
                  {{ $t('Bestellnummer') }} <span class="text-gray-400">({{ $t('optional') }})</span>
                </label>
                <input
                  type="text"
                  id="form1591-custom-order_number"
                  name="ticket[custom][order_number]"
                  v-model="form.orderNumber"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sonnenglas-500 focus:border-sonnenglas-500 transition-colors"
                  :placeholder="$t('z.B. #12345')"
                />
              </div>

              <!-- Subject Field -->
              <div>
                <label for="form1591-subject" class="block text-sm font-medium text-gray-700 mb-2">
                  {{ $t('Betreff') }}
                </label>
                <input
                  type="text"
                  id="form1591-subject"
                  name="ticket[subject]"
                  v-model="form.subject"
                  required
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sonnenglas-500 focus:border-sonnenglas-500 transition-colors"
                  :placeholder="$t('Worum geht es?')"
                />
              </div>

              <!-- Message Field -->
              <div>
                <label for="form1591-message" class="block text-sm font-medium text-gray-700 mb-2">
                  {{ $t('Nachricht') }}
                </label>
                <textarea
                  id="form1591-message"
                  name="ticket[message]"
                  v-model="form.message"
                  required
                  rows="6"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sonnenglas-500 focus:border-sonnenglas-500 transition-colors resize-vertical"
                  :placeholder="$t('Beschreiben Sie Ihr Anliegen...')"
                ></textarea>
              </div>

              <!-- Submit Button -->
              <div>
                <input
                  type="submit"
                  name="send"
                  :value="trans('Nachricht senden')"
                  :disabled="submitting"
                  class="btn btn-sonnenglas w-full py-3 px-6 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                />
                <div v-if="submitting" class="flex items-center justify-center mt-2 text-gray-600">
                  <svg class="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ $t('Wird gesendet...') }}
                </div>
              </div>
            </form>
          </div>

          <!-- Contact Information Section -->
          <div class="space-y-8">
            <!-- Contact Methods -->
            <div class="bg-white rounded-xl shadow-lg p-8">
              <h2 class="text-2xl font-bold text-gray-900 mb-6">{{ $t('Kontaktmöglichkeiten') }}</h2>
              
              <div class="space-y-6">
                <!-- Email -->
                <div class="flex items-start space-x-4">
                  <div class="flex-shrink-0">
                    <div class="w-10 h-10 bg-sonnenglas-100 rounded-lg flex items-center justify-center">
                      <svg class="w-5 h-5 text-sonnenglas-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900">{{ $t('E-Mail Support') }}</h3>
                    <p class="text-gray-600 mb-2">{{ $t('Für alle Fragen rund um Bestellungen und Produkte') }}</p>
                    <a :href="`mailto:${contactInfo.email}`" class="text-sonnenglas-600 hover:text-sonnenglas-700 font-medium">
                      {{ contactInfo.email }}
                    </a>
                  </div>
                </div>

                <!-- Phone -->
                <div class="flex items-start space-x-4">
                  <div class="flex-shrink-0">
                    <div class="w-10 h-10 bg-sonnenglas-100 rounded-lg flex items-center justify-center">
                      <svg class="w-5 h-5 text-sonnenglas-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900">{{ $t('Telefonischer Support') }}</h3>
                    <p v-if="contactInfo.business_hours" class="text-gray-600 mb-2">{{ contactInfo.business_hours }}</p>
                    <p v-else class="text-gray-600 mb-2">{{ $t('Montag bis Freitag, 9:00 - 17:00 Uhr') }}</p>
                    <a :href="`tel:${contactInfo.phone_link}`" class="text-sonnenglas-600 hover:text-sonnenglas-700 font-medium">
                      {{ contactInfo.phone }}
                    </a>
                  </div>
                </div>

                <!-- Office Address -->
                <div class="flex items-start space-x-4">
                  <div class="flex-shrink-0">
                    <div class="w-10 h-10 bg-sonnenglas-100 rounded-lg flex items-center justify-center">
                      <svg class="w-5 h-5 text-sonnenglas-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900">{{ $t('Anschrift') }}</h3>
                    <div class="text-gray-600 space-y-1">
                      <p>{{ contactInfo.company_name }}</p>
                      <p v-if="contactInfo.company_info" class="text-sm">{{ contactInfo.company_info }}</p>
                      <p v-if="contactInfo.registered_office" class="text-sm">{{ contactInfo.registered_office }}</p>
                      <p v-if="contactInfo.operations_site" class="text-sm mt-2">{{ $t('Betriebsstätte') }}: {{ contactInfo.operations_site }}</p>
                      <template v-if="contactInfo.street">
                        <p>{{ contactInfo.street }}</p>
                        <p>{{ contactInfo.city }}<span v-if="contactInfo.location">, {{ contactInfo.location }}</span></p>
                      </template>
                      <p v-if="contactInfo.country">{{ contactInfo.country }}</p>
                      <div v-if="contactInfo.legal_info" class="text-sm mt-3 space-y-1">
                        <p v-if="contactInfo.legal_info.ust_id">{{ $t('USt-IdNr') }}: {{ contactInfo.legal_info.ust_id }}</p>
                        <p v-if="contactInfo.legal_info.amtsgericht">{{ $t('Amtsgericht') }}: {{ contactInfo.legal_info.amtsgericht }}</p>
                        <p v-if="contactInfo.legal_info.weee_reg_nr">{{ $t('WEEE-Reg.-Nr.') }} {{ contactInfo.legal_info.weee_reg_nr }}</p>
                        <p v-if="contactInfo.legal_info.registrierung_gem_battg">{{ $t('Registrierung gem. BattG') }} - {{ contactInfo.legal_info.registrierung_gem_battg }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Links -->
            <div class="bg-white rounded-xl shadow-lg p-8">
              <h2 class="text-2xl font-bold text-gray-900 mb-6">{{ $t('Häufige Themen') }}</h2>
              
              <div class="space-y-4">
                <Link 
                  :href="route('faq')"
                  class="block p-4 rounded-lg border border-gray-200 hover:border-sonnenglas-300 hover:shadow-md transition-all group"
                >
                  <div class="flex items-center justify-between">
                    <div>
                      <h3 class="font-semibold text-gray-900 group-hover:text-sonnenglas-700">{{ $t('Häufig gestellte Fragen') }}</h3>
                      <p class="text-sm text-gray-600">{{ $t('Antworten auf die wichtigsten Fragen') }}</p>
                    </div>
                    <svg class="w-5 h-5 text-gray-400 group-hover:text-sonnenglas-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </div>
                </Link>

                <Link 
                  :href="route('files')"
                  class="block p-4 rounded-lg border border-gray-200 hover:border-sonnenglas-300 hover:shadow-md transition-all group"
                >
                  <div class="flex items-center justify-between">
                    <div>
                      <h3 class="font-semibold text-gray-900 group-hover:text-sonnenglas-700">{{ $t('Downloads & Materialien') }}</h3>
                      <p class="text-sm text-gray-600">{{ $t('Produktbilder, Anleitungen und mehr') }}</p>
                    </div>
                    <svg class="w-5 h-5 text-gray-400 group-hover:text-sonnenglas-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </div>
                </Link>

                <Link 
                  :href="route('ordersList')"
                  class="block p-4 rounded-lg border border-gray-200 hover:border-sonnenglas-300 hover:shadow-md transition-all group"
                >
                  <div class="flex items-center justify-between">
                    <div>
                      <h3 class="font-semibold text-gray-900 group-hover:text-sonnenglas-700">{{ $t('Meine Bestellungen') }}</h3>
                      <p class="text-sm text-gray-600">{{ $t('Status und Verlauf Ihrer Bestellungen') }}</p>
                    </div>
                    <svg class="w-5 h-5 text-gray-400 group-hover:text-sonnenglas-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { Link } from '@inertiajs/vue3'
import { trans } from 'laravel-vue-i18n'
import AppLayout from '@/layouts/AppLayout.vue'

interface ContactInfo {
  email: string
  phone: string
  phone_link: string
  business_hours?: string | null
  company_name: string
  company_info?: string
  registered_office?: string
  operations_site?: string
  street?: string
  city?: string
  location?: string
  country?: string
  legal_info?: {
    ust_id?: string
    amtsgericht?: string
    weee_reg_nr?: string
    registrierung_gem_battg?: string
  }
}

interface Props {
  customerName: string
  customerNumber: string
  userName: string
  userEmail: string
  orderNumber?: string
  contactInfo: ContactInfo
}

const props = defineProps<Props>()

const form = reactive({
  name: props.userName,
  email: props.userEmail,
  customerName: props.customerName,
  customerNumber: props.customerNumber,
  orderNumber: props.orderNumber || '',
  subject: '',
  message: ''
})

const submitting = ref(false)

const handleSubmit = (event: Event) => {
  submitting.value = true
  // The form will submit normally to the external service
  // We just show loading state during submission
}
</script>